<script setup lang="ts">
import { computed, ref, watch, toRef } from 'vue';
import TabEventBasics from './TabEventBasics.vue';
import TabTicketTiers from './Tiers.vue';
import TabAddOns from './TabAddOns.vue';
import TabGallery from './TabGallery.vue';
import TabFaq from './TabFaq.vue';
import TabMessages from './TabMessages.vue';

const props = defineProps<{
  causes: string[];
  availableTags: string[];
  causeLoading: boolean;
  isUploading: boolean;
  showErrors: boolean;
  validationErrors: string[];
  isEditPage?: boolean;
  stickerStatus?: string | null;
  stickerId?: string | null;
}>();

const emit = defineEmits(['onFetchCauses', 'saveDraft', 'publishEvent']);

type TabKey = 'event-basics' | 'ticket-tiers' | 'add-ons' | 'gallery' | 'faq' | 'messages';

interface Tab {
  key: TabKey;
  label: string;
  step: number;
  percent: number;
}

const tabs: Tab[] = [
  { key: 'event-basics', label: 'Event Basics', step: 1, percent: 17 },
  { key: 'ticket-tiers', label: 'Ticket Tiers', step: 2, percent: 33 },
  { key: 'add-ons', label: 'Add-Ons', step: 3, percent: 50 },
  { key: 'gallery', label: 'Gallery', step: 4, percent: 67 },
  { key: 'faq', label: 'FAQ', step: 5, percent: 83 },
  { key: 'messages', label: 'Messages', step: 6, percent: 100 },
];

const activeTab = ref<TabKey>('event-basics');

const currentTab = computed(() => tabs.find((t) => t.key === activeTab.value) || tabs[0]);

const isPublishedEvent = computed(() => props.stickerStatus === 'Active');

const progressMetaLabel = computed(() => {
  if (props.isEditPage) {
    return `Editing: ${currentTab.value.label}`;
  }
  return `Step ${currentTab.value.step} of ${tabs.length}`;
});

const progressPercent = computed(() => {
  if (props.isEditPage) {
    return 100;
  }
  return currentTab.value.percent;
});

const progressPercentLabel = computed(() => {
  if (props.isEditPage) {
    return 'All sections available';
  }
  return `${currentTab.value.percent}% Complete`;
});

const draftButtonLabel = computed(() => (
  props.isEditPage ? 'Save Changes' : 'Save Draft'
));

const primaryButtonLabel = computed(() => {
  if (props.isEditPage && isPublishedEvent.value) {
    return 'Update Event';
  }
  return 'Publish Event';
});

const tabStorageKey = computed(() => (
  props.stickerId ? `ticket-builder-tab-${props.stickerId}` : null
));

function readStoredTab(): TabKey | null {
  if (!tabStorageKey.value || typeof sessionStorage === 'undefined') {
    return null;
  }
  const stored = sessionStorage.getItem(tabStorageKey.value);
  if (stored && tabs.some((t) => t.key === stored)) {
    return stored as TabKey;
  }
  return null;
}

watch(
  () => props.stickerId,
  (id) => {
    if (!id || !props.isEditPage) {
      return;
    }
    const stored = readStoredTab();
    if (stored) {
      activeTab.value = stored;
    }
  },
  { immediate: true },
);

watch(activeTab, (tab) => {
  if (!tabStorageKey.value || typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(tabStorageKey.value, tab);
});

const actionsLocked = ref(false);
const isUploadingRef = toRef(props, 'isUploading');

watch(isUploadingRef, (uploading) => {
  if (!uploading) {
    actionsLocked.value = false;
  }
});

const isActionDisabled = computed(() => props.isUploading || actionsLocked.value);

const onSaveDraft = () => {
  if (isActionDisabled.value) return;
  actionsLocked.value = true;
  emit('saveDraft');
};

const onPublishEvent = () => {
  if (isActionDisabled.value) return;
  actionsLocked.value = true;
  emit('publishEvent');
};
</script>

<template lang="pug">
section.c-ticket-builder
  //- Progress bar
  .c-ticket-builder__progress
    .c-ticket-builder__progress-meta
      span.c-ticket-builder__step-label {{ progressMetaLabel }}
      span.c-ticket-builder__percent-label {{ progressPercentLabel }}
    .c-ticket-builder__progress-bar
      .c-ticket-builder__progress-fill(:style="{ width: progressPercent + '%' }")

  //- Tab navigation
  .c-ticket-builder__tabs-wrap
    nav.c-ticket-builder__tabs
      button.c-ticket-builder__tab(
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        :class="{ 'c-ticket-builder__tab--active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      ) {{ tab.label }}

  //- Tab content
  .c-ticket-builder__content
    TabEventBasics(
      v-if="activeTab === 'event-basics'"
      :show-errors="showErrors"
      :validation-errors="validationErrors"
    )
    TabTicketTiers(v-if="activeTab === 'ticket-tiers'")
    TabAddOns(v-if="activeTab === 'add-ons'")
    TabGallery(v-if="activeTab === 'gallery'")
    TabFaq(v-if="activeTab === 'faq'")
    TabMessages(
      v-if="activeTab === 'messages'"
      :is-published="isPublishedEvent"
      :sticker-id="stickerId"
    )

  //- Bottom action buttons
  .c-ticket-builder__actions
    button.c-ticket-builder__btn.c-ticket-builder__btn--draft(
      type="button"
      :disabled="isActionDisabled"
      @click="onSaveDraft"
    ) {{ draftButtonLabel }}
    button.c-ticket-builder__btn.c-ticket-builder__btn--publish(
      type="button"
      :disabled="isActionDisabled"
      @click="onPublishEvent"
    )
      span(v-if="isUploading") Saving...
      span(v-else) {{ primaryButtonLabel }}
</template>

<style lang="scss" scoped>
.c-ticket-builder {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 340px;

  // Progress bar
  &__progress {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__progress-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__step-label {
    font-size: 13px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    color: #222;
  }

  &__percent-label {
    font-size: 13px;
    color: #888;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__progress-bar {
    width: 100%;
    height: 6px;
    background: #e8e8e8;
    border-radius: 3px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    background: #1ebeaf;
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  // Tab nav
  &__tabs-wrap {
    background-color: #f3f4f6;
    border-radius: 12px;
    padding: 4px;
  }

  &__tabs {
    display: inline-flex;
    width: 100%;
    gap: 0;
    flex-wrap: wrap;
  }

  &__tab {
    flex: 1;
    background: none;
    border: none;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Red Hat Display', sans-serif;
    color: #4b5563;
    padding: 7px 14px;
    cursor: pointer;
    border-radius: 10px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    user-select: none;

    &:hover:not(.c-ticket-builder__tab--active) {
      color: #111827;
      background-color: rgba(255, 255, 255, 0.5);
    }

    &--active {
      background-color: #fff;
      color: #111827;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    }
  }

  // Tab content
  &__content {
    min-height: 300px;
  }

  // Bottom action buttons
  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
    margin-top: 4px;
  }

  &__btn {
    height: 42px;
    padding: 0 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    font-family: 'Red Hat Display', sans-serif;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, opacity 0.2s;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--draft {
      background: #fff;
      border: 1.5px solid #bbb;
      color: #333;

      &:hover:not(:disabled) {
        border-color: #888;
        color: #111;
      }
    }

    &--publish {
      background: #1ebeaf;
      border: 1.5px solid #1ebeaf;
      color: #fff;

      &:hover:not(:disabled) {
        background: #17a99b;
        border-color: #17a99b;
      }
    }
  }
}
</style>
